import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import { api } from "../lib/api";
import {
  Loader2, AlertTriangle, FileDown, CheckCircle2, XCircle, Clock,
  TrafficCone, ShieldAlert,
} from "lucide-react";

const STATUS_META = {
  approved: { color: "#34D399", bg: "#0A1E1A", label: "Approved",  icon: CheckCircle2 },
  rejected: { color: "#F87171", bg: "#1A0A0A", label: "Rejected",  icon: XCircle },
  deferred: { color: "#FBBF24", bg: "#1A1A0A", label: "Deferred",  icon: Clock },
  pending:  { color: "#94A3B8", bg: "#0F172A", label: "Pending",   icon: Clock },
};

const OPTION_BORDER = {
  option_a: "border-emerald-400/60",
  option_b: "border-amber-400/60",
  option_c: "border-rose-400/60",
};
const OPTION_LABEL_COLOR = {
  option_a: "text-emerald-400",
  option_b: "text-amber-400",
  option_c: "text-rose-400",
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
      style={{ color: m.color, backgroundColor: m.bg, border: `1px solid ${m.color}40` }}
      data-testid={`status-badge-${status}`}
    >
      <Icon size={12} /> {m.label}
    </span>
  );
}

function OptionCard({ optKey, opt }) {
  if (!opt) return null;
  return (
    <div
      className={`rounded-xl border-2 ${OPTION_BORDER[optKey]} bg-navy-deep/60 p-4 flex flex-col gap-2.5`}
      data-testid={`opt-card-${optKey}`}
    >
      <div className={`text-[10px] uppercase tracking-[0.22em] font-bold ${OPTION_LABEL_COLOR[optKey]}`}>
        {optKey.replace("_", " ")}
      </div>
      <div className="text-cream font-semibold text-sm leading-tight">{opt.label}</div>
      <div className="text-cream-dim text-xs leading-relaxed">{opt.description}</div>
      <div className="grid grid-cols-1 gap-1.5 mt-1 text-[11px]">
        <div><span className="text-cream-dim/70">Cost:</span> <span className="text-cream">{opt.cost_range}</span></div>
        <div><span className="text-cream-dim/70">Safety:</span> <span className="text-cream">{opt.safety_impact}</span></div>
        <div><span className="text-cream-dim/70">Traffic:</span> <span className="text-cream">{opt.congestion_impact}</span></div>
        <div><span className="text-cream-dim/70">Confidence:</span> <span className="text-cream">{opt.confidence}</span></div>
      </div>
      <div className="text-[10px] text-cream-dim/60 mt-1 italic border-t border-gold-dim/20 pt-2">
        {opt.standards_ref}
      </div>
    </div>
  );
}

function RecommendationCard({ rec, onAction }) {
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(null);
  const status = (rec.approval_status || "pending").toLowerCase();

  const submit = async (action) => {
    setLoading(action);
    try {
      await api.post(`/auditiq/traffic/recommendations/${rec.id}/approve`, {
        action,
        decision: action,
        notes,
        selected_option: rec.recommended_option,
      });
      onAction();
    } catch (e) {
      // surfaced via parent toast / banner if desired
      alert(e?.response?.data?.detail || "Action failed");
    } finally {
      setLoading(null);
    }
  };

  return (
    <article
      className="rounded-2xl border border-gold-dim/30 bg-navy-card p-5 sm:p-6 space-y-5"
      data-testid={`rec-card-${rec.id}`}
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-cream font-cinzel text-xl">
            {rec.intersection_name || rec.intersection_id}
          </h3>
          <div className="text-cream-dim text-xs mt-1">
            {rec.recommendation_type?.replace(/_/g, " ")} · {rec.issue_count} issue{rec.issue_count === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider"
            style={{
              color: rec.priority === "High" ? "#F87171" : "#FBBF24",
              backgroundColor: rec.priority === "High" ? "#1A0A0A" : "#1A1A0A",
              border: `1px solid ${rec.priority === "High" ? "#F8717160" : "#FBBF2460"}`,
            }}
          >
            {rec.priority} Priority
          </span>
          <StatusBadge status={status} />
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="opt-grid">
        <OptionCard optKey="option_a" opt={rec.option_a} />
        <OptionCard optKey="option_b" opt={rec.option_b} />
        <OptionCard optKey="option_c" opt={rec.option_c} />
      </div>

      <div className="text-[11px] text-emerald-400 font-bold uppercase tracking-wider">
        Recommended: {String(rec.recommended_option || "option_a").replace("_", " ")}
      </div>

      {rec.safety_note && (
        <div
          className="text-[11px] text-gold/90 italic border-l-2 border-gold pl-3 py-1.5"
          data-testid="rec-safety-note"
        >
          {rec.safety_note}
        </div>
      )}

      {status === "pending" ? (
        <div className="space-y-3 border-t border-gold-dim/20 pt-4">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Reviewer notes (optional)..."
            rows={2}
            className="w-full bg-navy-deep border border-gold-dim/30 rounded-lg px-3 py-2 text-sm text-cream placeholder-cream-dim/50 focus:outline-none focus:border-gold"
            data-testid={`rec-notes-${rec.id}`}
          />
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => submit("approved")}
              disabled={loading !== null}
              className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-navy-deep font-semibold text-sm transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              data-testid={`rec-approve-${rec.id}`}
            >
              {loading === "approved" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Approve
            </button>
            <button
              onClick={() => submit("rejected")}
              disabled={loading !== null}
              className="px-4 py-2 rounded-lg bg-rose-500 hover:bg-rose-400 text-cream font-semibold text-sm transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              data-testid={`rec-reject-${rec.id}`}
            >
              {loading === "rejected" ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
              Reject
            </button>
            <button
              onClick={() => submit("deferred")}
              disabled={loading !== null}
              className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-navy-deep font-semibold text-sm transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              data-testid={`rec-defer-${rec.id}`}
            >
              {loading === "deferred" ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />}
              Defer
            </button>
          </div>
        </div>
      ) : (
        <div className="border-t border-gold-dim/20 pt-3 text-xs text-cream-dim space-y-1" data-testid={`rec-history-${rec.id}`}>
          <div><strong className="text-cream">Reviewed by:</strong> {rec.approved_by || "—"}</div>
          <div><strong className="text-cream">When:</strong> {rec.approved_at ? String(rec.approved_at).slice(0, 19).replace("T", " ") : "—"}</div>
          {rec.selected_option && (
            <div><strong className="text-cream">Selected:</strong> {String(rec.selected_option).replace("_", " ")}</div>
          )}
        </div>
      )}
    </article>
  );
}

function AuditPicker({ jobs, selectedId, onSelect }) {
  return (
    <div className="rounded-2xl border border-gold-dim/30 bg-navy-card p-5">
      <div className="text-cream-dim text-xs uppercase tracking-[0.2em] mb-3">Select traffic audit</div>
      {jobs.length === 0 ? (
        <div className="text-cream-dim text-sm">
          No traffic audits found. Run an audit using the <Link to="/auditiq/upload" className="text-gold underline">Traffic & Intersection</Link> template first.
        </div>
      ) : (
        <select
          value={selectedId || ""}
          onChange={(e) => onSelect(e.target.value)}
          className="w-full bg-navy-deep border border-gold-dim/30 rounded-lg px-3 py-2 text-cream text-sm focus:outline-none focus:border-gold"
          data-testid="audit-picker"
        >
          <option value="">— Select an audit —</option>
          {jobs.map((j) => (
            <option key={j.audit_id} value={j.audit_id}>
              {j.audit_id.slice(0, 8)} · {j.organization_id || "—"} · {(j.created_at || "").slice(0, 16)}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

export default function TrafficRecommendations() {
  const [params, setParams] = useSearchParams();
  const auditId = params.get("audit_id");

  const [jobs, setJobs] = useState([]);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Load list of traffic audits
  useEffect(() => {
    api.get("/auditiq/jobs").then((r) => {
      const all = r.data?.jobs || [];
      setJobs(all.filter((j) => j.template_key === "traffic_intersection_template"));
    }).catch(() => {});
  }, []);

  const loadRecs = useCallback(async () => {
    if (!auditId) {
      setRecs([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/auditiq/traffic/recommendations/${auditId}`);
      setRecs(r.data?.recommendations || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load recommendations");
    } finally {
      setLoading(false);
    }
  }, [auditId]);

  useEffect(() => { loadRecs(); }, [loadRecs]);

  const exportPdf = async () => {
    if (!auditId) return;
    setExporting(true);
    try {
      const r = await api.get(`/auditiq/traffic/report/${auditId}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `AuditIQ_Traffic_Council_Report_${auditId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e?.response?.data?.detail || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  const counts = recs.reduce(
    (acc, r) => {
      const s = (r.approval_status || "pending").toLowerCase();
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    { pending: 0, approved: 0, rejected: 0, deferred: 0 }
  );

  return (
    <PortalLayout>
      <div className="space-y-6" data-testid="traffic-recs-page">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-electric text-xs uppercase tracking-[0.28em] mb-1 inline-flex items-center gap-2">
              <TrafficCone size={14} /> AuditIQ · Traffic Intelligence
            </div>
            <h1 className="text-cream font-cinzel text-3xl sm:text-4xl">
              Traffic Recommendations Review
            </h1>
            <p className="text-cream-dim text-sm mt-1">
              Council-ready review of traffic safety, congestion, and signal-timing recommendations.
            </p>
          </div>
          {auditId && (
            <button
              onClick={exportPdf}
              disabled={exporting || recs.length === 0}
              className="px-4 py-2 rounded-lg bg-gold hover:bg-gold/90 text-navy-deep font-semibold text-sm transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="export-council-pdf-btn"
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
              Export Council Review Packet
            </button>
          )}
        </header>

        {/* Disclaimer banner */}
        <div
          className="rounded-xl border border-gold/60 bg-gold/5 p-4 text-gold text-sm font-semibold flex items-start gap-3"
          data-testid="decision-support-disclaimer"
        >
          <ShieldAlert size={18} className="shrink-0 mt-0.5" />
          <span>
            DECISION SUPPORT ONLY — All recommendations require review and approval by a licensed
            traffic engineer or authorized DOT official before implementation. AuditIQ does not push
            changes to live signal controllers.
          </span>
        </div>

        {!auditId && (
          <AuditPicker
            jobs={jobs}
            selectedId={auditId}
            onSelect={(id) => setParams(id ? { audit_id: id } : {})}
          />
        )}

        {auditId && (
          <>
            <div className="flex flex-wrap gap-2 items-center">
              <button
                onClick={() => setParams({})}
                className="text-electric hover:text-gold text-xs underline"
                data-testid="back-to-audit-list"
              >
                ← Pick a different audit
              </button>
              <div className="flex-1" />
              <div className="flex gap-2 text-xs">
                {(["pending", "approved", "rejected", "deferred"]).map((k) => (
                  <span key={k} className="px-2 py-1 rounded bg-navy-card border border-gold-dim/20" data-testid={`count-${k}`}>
                    <span className="text-cream-dim mr-1">{k}:</span>
                    <strong className="text-cream">{counts[k] || 0}</strong>
                  </span>
                ))}
              </div>
            </div>

            {loading && (
              <div className="flex items-center justify-center py-16 text-cream-dim">
                <Loader2 className="animate-spin mr-2" size={18} /> Loading recommendations...
              </div>
            )}
            {error && (
              <div className="rounded-xl border border-rose-500/50 bg-rose-500/10 p-4 text-rose-400 text-sm flex items-center gap-2">
                <AlertTriangle size={16} /> {error}
              </div>
            )}

            {!loading && !error && recs.length === 0 && (
              <div className="rounded-xl border border-gold-dim/30 bg-navy-card p-8 text-center text-cream-dim text-sm">
                No traffic recommendations on this audit yet.
              </div>
            )}

            <div className="space-y-5">
              {recs.map((r) => (
                <RecommendationCard key={r.id} rec={r} onAction={loadRecs} />
              ))}
            </div>
          </>
        )}
      </div>
    </PortalLayout>
  );
}
